import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BellRing, ShieldAlert, Wrench, FileSignature, CalendarClock, Cake, CheckSquare, ChevronRight,
} from "lucide-react";
import api from "../lib/api";
import { fmtDate } from "../lib/format";
import PageHeader from "../components/PageHeader";

const META = {
  Warranty: { icon: ShieldAlert, color: "text-red-600 bg-red-50 border-red-200", route: "/devices" },
  Service: { icon: Wrench, color: "text-amber-600 bg-amber-50 border-amber-200", route: "/devices" },
  Contract: { icon: FileSignature, color: "text-violet-600 bg-violet-50 border-violet-200", route: "/clinics" },
  "Follow-up": { icon: CalendarClock, color: "text-brand bg-brand-light border-blue-200", route: "/meetings" },
  Birthday: { icon: Cake, color: "text-pink-600 bg-pink-50 border-pink-200", route: "/contacts" },
  Task: { icon: CheckSquare, color: "text-emerald-600 bg-emerald-50 border-emerald-200", route: "/tasks" },
};

function whenLabel(d) {
  if (d.overdue) return `${Math.abs(d.days_until)}d overdue`;
  if (d.days_until === 0) return "Today";
  if (d.days_until === 1) return "Tomorrow";
  return `in ${d.days_until} days`;
}

export default function Reminders() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("All");
  const [window, setWindow] = useState(30);

  useEffect(() => { api.get(`/reminders?window=${window}`).then((r) => setData(r.data)); }, [window]);
  if (!data) return <div className="text-slate-400">Loading reminders…</div>;

  const cats = ["All", ...Object.keys(META).filter((c) => data.counts[c])];
  const items = data.items.filter((i) => filter === "All" || i.category === filter);

  return (
    <div>
      <PageHeader title="Reminders" subtitle="Automated alerts across warranties, contracts, follow-ups & birthdays" icon={BellRing}>
        <div className="flex rounded-lg border border-slate-200 p-0.5 bg-white">
          {[14, 30, 90].map((w) => (
            <button key={w} onClick={() => setWindow(w)} data-testid={`window-${w}`}
              className={`px-3 py-1.5 rounded-md text-sm ${window === w ? "bg-navy-900 text-white" : "text-slate-500"}`}>
              {w}d
            </button>
          ))}
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <SummaryCard label="Total" value={data.total} active={filter === "All"} onClick={() => setFilter("All")} />
        {Object.keys(META).map((c) => (
          <SummaryCard key={c} label={c} value={data.counts[c] || 0} icon={META[c].icon}
            active={filter === c} onClick={() => setFilter(data.counts[c] ? c : filter)} />
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-50">
        {items.length === 0 && <div className="p-12 text-center text-slate-400">No reminders in this window 🎉</div>}
        {items.map((it, idx) => {
          const m = META[it.category] || META.Task;
          const Icon = m.icon;
          return (
            <div key={idx} onClick={() => navigate(m.route)} data-testid={`reminder-${idx}`}
              className="flex items-center gap-4 p-4 hover:bg-slate-50/70 cursor-pointer transition-colors">
              <div className={`h-10 w-10 rounded-lg border flex items-center justify-center shrink-0 ${m.color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-navy-900 truncate">{it.title}</div>
                <div className="text-xs text-slate-400 truncate">{it.subtitle} · {fmtDate(it.date)}</div>
              </div>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${it.overdue ? "bg-red-50 text-red-600 border-red-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                {whenLabel(it)}
              </span>
              <ChevronRight className="h-4 w-4 text-slate-300" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, active, onClick }) {
  return (
    <button onClick={onClick} data-testid={`summary-${label.toLowerCase()}`}
      className={`text-left rounded-xl border p-3.5 transition-all ${active ? "bg-navy-900 border-navy-900 text-white" : "bg-white border-slate-200 hover:border-slate-300"}`}>
      <div className="flex items-center justify-between">
        <span className={`text-xs font-medium ${active ? "text-blue-200" : "text-slate-400"}`}>{label}</span>
        {Icon && <Icon className={`h-3.5 w-3.5 ${active ? "text-blue-200" : "text-slate-300"}`} />}
      </div>
      <div className={`font-display text-2xl font-bold mt-1 ${active ? "text-white" : "text-navy-900"}`}>{value}</div>
    </button>
  );
}
